import React, { useEffect, useState } from 'react'
import Button from '../../ui/Button'
import Badge from '../../ui/Badge'
import type { BadgeVariant } from '../../ui/Badge'

export type DraftLineItem = Record<string, unknown> & {
  confidence?: 'high' | 'medium' | 'low' | string
  citation?: string | null
}

export interface DocumentImportDrawerProps {
  open: boolean
  title: string
  lineItems: DraftLineItem[]
  RowEditor: React.ComponentType<{ row: DraftLineItem; index: number; onChange: (patch: Partial<DraftLineItem>) => void }>
  onConfirm: (selectedRows: DraftLineItem[]) => void
  onClose: () => void
  rowErrors?: Record<number, string[]>
  busy?: boolean
  // Optional control rendered under the title — used by the AI importer to
  // show "Detected: <kind>" with an override dropdown.
  headerExtra?: React.ReactNode
}

const confidenceVariant = (c?: string): BadgeVariant =>
  c === 'high' ? 'green' : c === 'medium' ? 'amber' : 'gray'

export const DocumentImportDrawer = ({
  open, title, lineItems, RowEditor, onConfirm, onClose, rowErrors = {}, busy = false, headerExtra,
}: DocumentImportDrawerProps) => {
  const [rows, setRows] = useState<DraftLineItem[]>(lineItems)
  const [selected, setSelected] = useState<Set<number>>(new Set(lineItems.map((_, i) => i)))

  // The drawer is mounted once and toggled via `open`, so a new draft's
  // line items arrive as a prop change after mount. Re-sync the editable
  // rows + default-all-selected when the incoming items change (all rows
  // selected by default, mirroring the initial state).
  useEffect(() => {
    setRows(lineItems)
    setSelected(new Set(lineItems.map((_, i) => i)))
  }, [lineItems])

  if (!open) return null

  const patchRow = (i: number, patch: Partial<DraftLineItem>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))

  const toggle = (i: number) =>
    setSelected((s) => {
      const next = new Set(s)
      next.has(i) ? next.delete(i) : next.add(i)
      return next
    })

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/30" data-testid="document-import-drawer">
      {/* Flex column so the header + action footer stay pinned while only the
          rows scroll — with many line items the Confirm button must stay
          reachable without scrolling to the bottom. */}
      <div className="flex h-full w-full max-w-3xl flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-100 p-6 pb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
            {headerExtra && <div className="mt-1">{headerExtra}</div>}
          </div>
          <button onClick={onClose} className="text-gray-500" aria-label="Close">&#x2715;</button>
        </div>
        <div className="flex-1 overflow-y-auto px-6">
          <table className="w-full text-sm">
            <tbody>
              {rows.map((row, i) => (
                <React.Fragment key={i}>
                  <tr className="border-b border-gray-100">
                    <td className="py-2 pr-2 align-top">
                      <input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)}
                        aria-label={`select row ${i}`} />
                    </td>
                    <RowEditor row={row} index={i} onChange={(patch) => patchRow(i, patch)} />
                    <td className="py-2 pl-2 align-top">
                      <Badge variant={confidenceVariant(row.confidence)} data-testid="document-import-confidence">
                        {row.confidence ?? 'unknown'}
                      </Badge>
                      {row.citation && <div className="mt-1 text-xs text-gray-400">{row.citation}</div>}
                    </td>
                  </tr>
                  {rowErrors[i]?.length ? (
                    <tr><td colSpan={3} className="pb-2 text-xs text-cerise-red-600">{rowErrors[i].join('; ')}</td></tr>
                  ) : null}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 p-6 pt-4">
          <Button tertiary onClick={onClose}>Cancel</Button>
          <Button
            data-testid="document-import-confirm"
            disabled={busy || selected.size === 0}
            onClick={() => onConfirm(rows.filter((_, i) => selected.has(i)))}
          >
            Confirm import ({selected.size})
          </Button>
        </div>
      </div>
    </div>
  )
}

export default DocumentImportDrawer
